<template>
  <div class="comment__item-description-content">
    <template v-if="!beforeHasDescription">
      <BaseSubHeading
        size="medium"
        weight="400"
        color="monochrome-06"
        class="comment__no-description"
      >
        {{ emptyLabel }}
      </BaseSubHeading>
      <icon
        data="@icon/arrow-up.svg"
        :fill="true"
        width="var(--icon-24)"
        height="var(--icon-24)"
        color="var(--monochrome-05)"
        class="comment__no-description-arrow"
      />
    </template>

    <ReferencedText
      :html="afterDescription"
      class="referenced-text"
    />

    <button
      type="button"
      class="link is-medium comment__view-changes-link"
      @click="toggleViewMore"
    >
      View what was changed
    </button>

    <Teleport to="body">
      <ModalItemDescriptionChanged :comment="comment" />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, PropType } from "vue"

import ModalItemDescriptionChanged from "@/components/Modal/ModalItemDescriptionChanged.vue"
import { ReferencedText } from "@/components/Reference"

import { Comment, CommentContentType } from "@/models"

import type { CommentItemAuditItem } from "@/models"

const props = defineProps({
  comment: {
    type: Object as PropType<Comment>,
    required: true
  }
})

const instance = getCurrentInstance()

const emptyLabel = computed(() => {
  switch (props.comment.type) {
    case CommentContentType.ACCEPTANCE_CRITERIA:
      return "No Acceptance Criteria"
    case CommentContentType.SPEC:
      return "No Spec"
    default:
      return "No Description"
  }
})

function getHtml(auditItem: Partial<CommentItemAuditItem> | null) {
  switch (props.comment.type) {
    case CommentContentType.ACCEPTANCE_CRITERIA:
      return auditItem?.acceptanceCriteria?.html ?? ""
    case CommentContentType.SPEC:
      return auditItem?.spec?.html ?? ""
    default:
      return auditItem?.structuredDescription?.html ?? ""
  }
}

const afterDescription = computed(() => getHtml(props.comment.after))

const beforeHasDescription = computed(() => {
  const html = getHtml(props.comment.before)
  return !!html.replace(/<[^>]*>/g, "").trim()
})

function toggleViewMore() {
  instance?.proxy?.$modal.show(`item-description-changed-${props.comment.id}`)
}
</script>

<style scoped lang="scss">
.comment__no-description {
  display: block;
  width: 100%;
  padding: 2px var(--step4);
  border-radius: var(--radius-item);
  background: var(--monochrome-02);
  color: var(--monochrome-06);
  text-align: center;
}

.comment__no-description-arrow {
  display: block;
  margin: var(--step2) auto;
  transform: rotate(180deg);
}

.dark {
  .comment__no-description {
    background: var(--monochrome-22);
    color: var(--monochrome-03);
  }
}
</style>
